<template>
  <div class="d-flex flex-column-fluid finance-request-approval-page">
    <div class="container">
      <div class="page-header mb-6">
        <div class="header-content">
          <div class="header-title">
            <h1 class="title">Approve finance request</h1>
            <p class="subtitle">Review request details and complete final approval</p>
          </div>
        </div>
      </div>

      <div v-if="loading" class="loading-state">
        <div class="spinner-border" role="status"></div>
        <div class="loading-text">Loading finance request...</div>
      </div>

      <div v-else-if="error" class="result-card error-card">
        <i class="las la-exclamation-circle result-icon"></i>
        <h2 class="result-title">Unable to load finance request</h2>
        <p class="result-message">{{ error }}</p>
      </div>

      <div v-else-if="financeRequest" class="approval-content">
        <div class="facility-card">
          <div class="card-header">
            <h5 class="card-title">Request details</h5>
            <span :class="['status-badge', statusBadgeClass]">{{ statusLabel }}</span>
          </div>
          <div class="card-body">
            <div class="details-grid">
              <div class="detail-item">
                <label>Borrower</label>
                <div class="value">{{ borrowerName }}</div>
              </div>
              <div class="detail-item">
                <label>Invoice reference</label>
                <div class="value">{{ financeRequest.invoice_reference || '—' }}</div>
              </div>
              <div class="detail-item">
                <label>Requested amount</label>
                <div class="value">{{ formatMoney(requestedAmount) }}</div>
              </div>
              <div class="detail-item">
                <label>Approved amount</label>
                <div class="value">{{ formatMoney(approvedAmount) }}</div>
              </div>
              <div class="detail-item">
                <label>Disbursement amount</label>
                <div class="value">{{ formatMoney(financeRequest.disbursement_amount) }}</div>
              </div>
              <div class="detail-item">
                <label>Advance rate</label>
                <div class="value">{{ formatPercent(advanceRate) }}</div>
              </div>
              <div class="detail-item">
                <label>Tenor</label>
                <div class="value">{{ formatTenor(tenorDays) }}</div>
              </div>
              <div class="detail-item">
                <label>Repayment date</label>
                <div class="value">
                  {{ formatDate(repaymentDate) }}
                  <span v-if="repaymentDate" class="value-hint">(assuming approval date is disbursal date)</span>
                </div>
              </div>
              <div class="detail-item">
                <label>Currency</label>
                <div class="value">{{ currency }}</div>
              </div>
              <div class="detail-item">
                <label>Transaction fee</label>
                <div class="value">{{ formatMoney(financeRequest.transaction_fee) }}</div>
              </div>
              <div class="detail-item">
                <label>Settlement fee</label>
                <div class="value">{{ formatMoney(financeRequest.settlement_fee) }}</div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="invoices.length" class="facility-card">
          <div class="card-header">
            <h5 class="card-title">Invoices</h5>
          </div>
          <div class="card-body invoice-table-wrap">
            <table class="invoice-table">
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Payer</th>
                  <th class="text-right">Requested</th>
                  <th class="text-right">Approved</th>
                  <th class="text-right">Advance rate</th>
                  <th class="text-right">Tenor</th>
                  <th class="text-right">Repayment date</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="invoice in invoices" :key="invoice.id || invoice.invoice_reference">
                  <td>{{ invoice.invoice_reference || '—' }}</td>
                  <td>{{ invoice.customer_name || '—' }}</td>
                  <td class="text-right">{{ formatMoney(getInvoiceRequestedAmount(invoice)) }}</td>
                  <td class="text-right">{{ formatMoney(getInvoiceApprovedAmount(invoice)) }}</td>
                  <td class="text-right">{{ formatPercent(invoice.advance_rate) }}</td>
                  <td class="text-right">{{ formatTenor(getInvoiceTenorDays(invoice)) }}</td>
                  <td class="text-right">{{ formatDate(getInvoiceRepaymentDate(invoice)) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-if="actionError" class="alert alert-danger action-error">
          <i class="material-icons">error</i>
          {{ actionError }}
        </div>

        <div v-if="alreadyApprovedNotice" class="alert alert-info action-error">
          <i class="las la-info-circle"></i>
          Already approved
        </div>

        <button
          v-if="isAwaitingApproval"
          type="button"
          class="btn btn-approve"
          :disabled="approving"
          @click="approveFinanceRequest"
        >
          <span v-if="approving" class="spinner-border spinner-border-sm mr-2"></span>
          <i v-else class="las la-check-circle"></i>
          {{ approving ? 'Approving...' : 'Approve finance request' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import FinancingService from "@/services/financing.service";
import CompaniesService from "@/services/companies.service";
import { formatErrorMessage } from "@/utils/random_utils";

const APPROVED_TOAST = "Finance request approved.";

export default {
  name: "FinanceRequestApproval",
  data() {
    return {
      loading: true,
      approving: false,
      error: null,
      actionError: null,
      alreadyApprovedNotice: false,
      financeRequest: null,
      borrowerName: "—"
    };
  },
  computed: {
    requestStatus() {
      if (!this.financeRequest || !this.financeRequest.status) return "";
      return String(this.financeRequest.status).trim().toUpperCase();
    },
    isAwaitingApproval() {
      return this.requestStatus === "AWAITING_APPROVAL";
    },
    isApproved() {
      return this.requestStatus === "APPROVED";
    },
    statusLabel() {
      if (this.isAwaitingApproval) return "Awaiting approval";
      if (this.isApproved) return "Approved";
      return this.requestStatus || "—";
    },
    statusBadgeClass() {
      if (this.isAwaitingApproval) return "status-awaiting";
      if (this.isApproved) return "status-approved";
      return "status-default";
    },
    currency() {
      return (this.financeRequest && this.financeRequest.currency) || "AED";
    },
    invoices() {
      if (!this.financeRequest || !Array.isArray(this.financeRequest.invoices)) return [];
      return this.financeRequest.invoices;
    },
    requestedAmount() {
      if (!this.financeRequest) return null;
      const fromRequest = this.firstNumber([
        this.financeRequest.requested_amount,
        this.financeRequest.total_requested_amount,
        this.financeRequest.initial_requested_amount
      ]);
      if (fromRequest !== null) return fromRequest;
      return this.sumInvoiceValues((invoice) => this.getInvoiceRequestedAmount(invoice));
    },
    approvedAmount() {
      if (!this.financeRequest) return null;
      const fromRequest = this.firstNumber([
        this.financeRequest.approval_amount,
        this.financeRequest.total_approval_amount,
        this.financeRequest.approved_amount,
        this.financeRequest.total_receivable_amount
      ]);
      if (fromRequest !== null) return fromRequest;
      return this.sumInvoiceValues((invoice) => this.getInvoiceApprovedAmount(invoice));
    },
    advanceRate() {
      if (!this.financeRequest) return null;
      const fromRequest = this.firstNumber([
        this.financeRequest.advance_rate,
        this.financeRequest.average_advance_rate
      ]);
      if (fromRequest !== null) return fromRequest;
      if (!this.invoices.length) return null;

      let weightedTotal = 0;
      let weight = 0;
      this.invoices.forEach((invoice) => {
        const rate = this.toNumber(invoice.advance_rate);
        const amount = this.getInvoiceApprovedAmount(invoice);
        if (rate === null) return;
        const amt = amount === null ? 0 : amount;
        weightedTotal += rate * (amt || 1);
        weight += amt || 1;
      });
      if (!weight) return null;
      return weightedTotal / weight;
    },
    tenorDays() {
      if (!this.financeRequest) return null;
      const fromRequest = this.tenorDaysFromRequestOnly();
      if (fromRequest !== null) return fromRequest;

      const invoiceTenors = this.invoices
        .map((invoice) => this.getInvoiceTenorDays(invoice, true))
        .filter((value) => value !== null);
      if (!invoiceTenors.length) return null;
      const unique = invoiceTenors.filter((value, index, arr) => arr.indexOf(value) === index);
      if (unique.length === 1) return unique[0];
      return Math.max.apply(null, unique);
    },
    approvalDate() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return today;
    },
    repaymentDate() {
      return this.addDays(this.approvalDate, this.tenorDays);
    }
  },
  mounted() {
    this.loadFinanceRequest();
  },
  methods: {
    async loadFinanceRequest() {
      const financeRequestId = this.$route.params.financeRequestId;
      this.loading = true;
      this.error = null;
      this.actionError = null;
      this.alreadyApprovedNotice = false;
      this.financeRequest = null;
      this.borrowerName = "—";

      if (!financeRequestId) {
        this.error = "Finance request not found";
        this.loading = false;
        return;
      }

      try {
        const response = await FinancingService.getFinanceRequest(financeRequestId);
        const request = FinancingService.unwrapEntity(response);
        if (!request || !request.id) {
          this.error = "Finance request not found";
          return;
        }

        if (String(request.id) !== String(financeRequestId)) {
          this.error = "This approval link does not match the finance request.";
          return;
        }

        this.financeRequest = request;
        await this.loadBorrowerName(request);
      } catch (error) {
        this.error = this.resolveLoadError(error);
      } finally {
        this.loading = false;
      }
    },

    async loadBorrowerName(request) {
      const borrowerId = (request && (request.borrower_id || request.business_id))
        || this.$route.params.businessId;
      if (!borrowerId) {
        this.borrowerName = "—";
        return;
      }

      try {
        const response = await CompaniesService.get(borrowerId, { list_view: true });
        const business = FinancingService.unwrapEntity(response) || (response && response.data);
        this.borrowerName = (business && business.name) || "—";
      } catch (error) {
        this.borrowerName = "—";
      }
    },

    async approveFinanceRequest() {
      if (this.approving || !this.isAwaitingApproval || !this.financeRequest) return;

      this.approving = true;
      this.actionError = null;
      this.alreadyApprovedNotice = false;

      try {
        const response = await FinancingService.approveFinanceRequest(this.financeRequest.id);
        const updated = FinancingService.unwrapEntity(response) || this.financeRequest;
        this.financeRequest = Object.assign({}, this.financeRequest, updated, {
          status: (updated && updated.status) || "APPROVED"
        });
        this.$showAlert(APPROVED_TOAST, "success");
      } catch (error) {
        const refreshed = await this.refreshApprovedState();
        if (refreshed || this.isAlreadyApprovedError(error)) {
          this.alreadyApprovedNotice = true;
          this.$showAlert("Already approved", "info");
          return;
        }
        this.actionError = this.resolveApproveError(error);
      } finally {
        this.approving = false;
      }
    },

    async refreshApprovedState() {
      const financeRequestId = this.$route.params.financeRequestId;
      if (!financeRequestId) return false;
      try {
        const response = await FinancingService.getFinanceRequest(financeRequestId);
        const request = FinancingService.unwrapEntity(response);
        if (!request) return false;
        this.financeRequest = Object.assign({}, this.financeRequest || {}, request);
        return String(request.status || "").toUpperCase() === "APPROVED";
      } catch (e) {
        return false;
      }
    },

    formatMoney(amount) {
      if (amount === undefined || amount === null || amount === "") return "—";
      const number = Number(amount);
      if (isNaN(number)) return String(amount);
      const formatted = number.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
      return `${formatted} ${this.currency}`;
    },

    formatPercent(value) {
      const number = this.toNumber(value);
      if (number === null) return "—";
      return `${number}%`;
    },

    formatTenor(days) {
      const number = this.toNumber(days);
      if (number === null) return "—";
      return number === 1 ? "1 day" : `${number} days`;
    },

    formatDate(date) {
      const parsed = this.parseDate(date);
      if (!parsed) return "—";
      return parsed.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric"
      });
    },

    toNumber(value) {
      if (value === undefined || value === null || value === "") return null;
      const number = Number(value);
      return isNaN(number) ? null : number;
    },

    firstNumber(values) {
      for (let i = 0; i < values.length; i += 1) {
        const number = this.toNumber(values[i]);
        if (number !== null) return number;
      }
      return null;
    },

    sumInvoiceValues(getter) {
      if (!this.invoices.length) return null;
      let total = 0;
      let hasValue = false;
      this.invoices.forEach((invoice) => {
        const value = getter(invoice);
        if (value === null) return;
        total += value;
        hasValue = true;
      });
      return hasValue ? total : null;
    },

    getInvoiceRequestedAmount(invoice) {
      return this.firstNumber([
        invoice.initial_requested_amount,
        invoice.requested_amount,
        invoice.invoice_amount
      ]);
    },

    getInvoiceApprovedAmount(invoice) {
      return this.firstNumber([
        invoice.approval_amount,
        invoice.approved_amount,
        invoice.purchase_amount
      ]);
    },

    getInvoiceTenorDays(invoice, skipRequestFallback) {
      if (!invoice) return skipRequestFallback ? null : this.tenorDaysFromRequestOnly();
      const extras = invoice.additional_fields || invoice.additional_details || {};
      const direct = this.firstNumber([
        extras.payment_period,
        invoice.payment_period,
        invoice.tenor,
        invoice.tenor_days,
        invoice.days,
        invoice.due_days,
        extras.tenor,
        extras.tenor_days
      ]);
      if (direct !== null) return direct;

      const fees = invoice.transaction_fees;
      if (fees && typeof fees === "object" && !Array.isArray(fees)) {
        const days = Object.keys(fees)
          .map((key) => Number(key))
          .filter((key) => !isNaN(key) && key > 0);
        if (days.length) return Math.max.apply(null, days);
      }
      return skipRequestFallback ? null : this.tenorDaysFromRequestOnly();
    },

    tenorDaysFromRequestOnly() {
      if (!this.financeRequest) return null;
      const extras = this.financeRequest.additional_fields || {};
      return this.firstNumber([
        extras.payment_period,
        this.financeRequest.payment_period,
        this.financeRequest.tenor,
        this.financeRequest.tenor_days,
        this.financeRequest.days,
        extras.tenor,
        extras.tenor_days
      ]);
    },

    getInvoiceRepaymentDate(invoice) {
      return this.addDays(this.approvalDate, this.getInvoiceTenorDays(invoice));
    },

    parseDate(value) {
      if (!value) return null;
      if (value instanceof Date) {
        return isNaN(value.getTime()) ? null : value;
      }
      const parsed = new Date(value);
      return isNaN(parsed.getTime()) ? null : parsed;
    },

    addDays(date, days) {
      const parsed = this.parseDate(date);
      const tenor = this.toNumber(days);
      if (!parsed || tenor === null) return null;
      const next = new Date(parsed.getTime());
      next.setDate(next.getDate() + tenor);
      return next;
    },

    getErrorText(error) {
      if (!error) return "";
      if (typeof error === "string") return error;
      if (error.message) return String(error.message);
      return formatErrorMessage(error) || "";
    },

    getErrorStatus(error) {
      if (!error) return 0;
      if (error.status) return error.status;
      if (error.response && error.response.status) return error.response.status;
      return 0;
    },

    isAlreadyApprovedError(error) {
      const text = this.getErrorText(error).toLowerCase();
      return text.indexOf("already approved") !== -1;
    },

    resolveLoadError(error) {
      const status = this.getErrorStatus(error);
      const text = this.getErrorText(error).toLowerCase();
      if (status === 404 || text.indexOf("not found") !== -1) {
        return "Finance request not found";
      }
      return formatErrorMessage(error) || "Failed to load this finance request. Please try again.";
    },

    resolveApproveError(error) {
      const status = this.getErrorStatus(error);
      const text = this.getErrorText(error).toLowerCase();
      if (status === 404 || text.indexOf("not found") !== -1) {
        return "Finance request not found";
      }
      if (text.indexOf("not awaiting") !== -1) {
        return "Finance request is not awaiting approval.";
      }
      return formatErrorMessage(error) || "Failed to approve finance request. Please try again.";
    }
  }
};
</script>

<style scoped lang="scss">
@import './index.scss';

.finance-request-approval-page {
  .container {
    max-width: 960px;
  }
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 1rem;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.loading-text {
  margin-top: 0.75rem;
  color: #64748b;
  font-size: 0.9375rem;
}

.result-card,
.facility-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.result-card {
  padding: 3rem 2rem;
  text-align: center;
}

.result-icon {
  font-size: 3rem;
  margin-bottom: 0.75rem;
}

.error-card .result-icon {
  color: #dc2626;
}

.result-title {
  margin: 0 0 0.5rem;
  font-size: 1.375rem;
  font-weight: 700;
  color: #0f172a;
}

.result-message {
  margin: 0;
  color: #64748b;
  font-size: 0.9375rem;
}

.approval-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.facility-card {
  .card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 1rem 1.25rem;
    border-bottom: 1px solid #e5e7eb;
  }

  .card-title {
    margin: 0;
    font-size: 1rem;
    font-weight: 700;
    color: #0f172a;
  }

  .card-body {
    padding: 1.25rem;
  }
}

.details-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem 1.25rem;
}

.detail-item {
  label {
    display: block;
    margin-bottom: 0.25rem;
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    color: #64748b;
  }

  .value {
    font-size: 0.9375rem;
    color: #0f172a;
    font-weight: 600;
  }
}

.value-hint {
  display: inline;
  margin-left: 0.25rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: #64748b;
}

.invoice-table-wrap {
  overflow-x: auto;
}

.invoice-table {
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 0.6rem 0.75rem;
    border-bottom: 1px solid #e5e7eb;
    font-size: 0.875rem;
  }

  th {
    text-align: left;
    color: #64748b;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  .text-right {
    text-align: right;
  }
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.7rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.status-awaiting {
  background: #fff7ed;
  color: #9a3412;
  border: 1px solid #fed7aa;
}

.status-approved {
  background: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.status-default {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.action-error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
}

.btn-approve {
  align-self: flex-start;
  background: #111827;
  border: none;
  color: #fff;
  font-weight: 600;
  padding: 0.75rem 1.25rem;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;

  &:hover:not(:disabled) {
    background: #1f2937;
    color: #fff;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  i {
    font-size: 1.1rem;
  }
}

@media (max-width: 640px) {
  .details-grid {
    grid-template-columns: 1fr;
  }

  .btn-approve {
    width: 100%;
    justify-content: center;
  }
}
</style>
